import { useCallback, useRef, useState } from 'react';
import {
  type DiagnosticSuiteResult,
  DiagnosticTest,
} from '../lib/diagnostic-test';

export type DiagnosticStatus = 'idle' | 'running' | 'completed' | 'error';

export interface DiagnosticProgress {
  testId: string;
  progress: number;
  message: string;
}

export interface UseDiagnosticTestOptions {
  baseUrl?: string;
  onProgress?: (progress: DiagnosticProgress) => void;
  onComplete?: (result: DiagnosticSuiteResult) => void;
  onError?: (error: Error) => void;
}

export interface UseDiagnosticTestReturn {
  status: DiagnosticStatus;
  progress: DiagnosticProgress | null;
  result: DiagnosticSuiteResult | null;
  error: Error | null;
  startDiagnostic: () => Promise<void>;
  abort: () => void;
  reset: () => void;
}

export function useDiagnosticTest(
  options: UseDiagnosticTestOptions = {}
): UseDiagnosticTestReturn {
  const [status, setStatus] = useState<DiagnosticStatus>('idle');
  const [progress, setProgress] = useState<DiagnosticProgress | null>(null);
  const [result, setResult] = useState<DiagnosticSuiteResult | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const diagnosticTestRef = useRef<DiagnosticTest | null>(null);

  const getBaseUrl = useCallback(() => {
    // Use same-origin for stable asset URLs via Pages Functions
    // The /asset/{id}/{instance} route proxies to R2 and provides full Resource Timing data
    // Default to current origin if no baseUrl provided
    return options.baseUrl || window.location.origin;
  }, [options.baseUrl]);

  const handleProgress = useCallback(
    (testId: string, progressValue: number, message: string) => {
      const progressData: DiagnosticProgress = {
        testId,
        progress: progressValue,
        message,
      };
      setProgress(progressData);
      options.onProgress?.(progressData);
    },
    [options]
  );

  const startDiagnostic = useCallback(async () => {
    console.log('[useDiagnosticTest] Starting diagnostic tests');
    setStatus('running');
    setError(null);
    setResult(null);
    setProgress(null);

    try {
      const baseUrl = getBaseUrl();
      console.log('[useDiagnosticTest] Using baseUrl:', baseUrl);

      diagnosticTestRef.current = new DiagnosticTest({
        baseUrl,
        onProgress: handleProgress,
      });

      console.log('[useDiagnosticTest] Running all diagnostic tests...');
      const diagnosticResult = await diagnosticTestRef.current.runAll();
      console.log('[useDiagnosticTest] Diagnostic tests completed', {
        testA: diagnosticResult.testA?.goodputOverallMbps,
        testB: diagnosticResult.testB?.goodputOverallMbps,
        testC: diagnosticResult.testC?.goodputOverallMbps,
        testD: diagnosticResult.testD?.goodputOverallMbps,
      });

      setResult(diagnosticResult);
      setStatus('completed');
      options.onComplete?.(diagnosticResult);
    } catch (err) {
      console.error('[useDiagnosticTest] Error during diagnostic tests:', err);
      const testError = err instanceof Error ? err : new Error('Unknown error');
      setError(testError);
      setStatus('error');
      options.onError?.(testError);
    }
  }, [getBaseUrl, handleProgress, options]);

  const abort = useCallback(() => {
    diagnosticTestRef.current?.abort();
    setStatus('idle');
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setProgress(null);
    setResult(null);
    setError(null);
    diagnosticTestRef.current = null;
  }, []);

  return {
    status,
    progress,
    result,
    error,
    startDiagnostic,
    abort,
    reset,
  };
}
